import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { format, formatDistanceToNow } from 'date-fns';

import { ConsignmentHistoryEntry } from '@/entities/consignments';
import { getCancellationReasonOptions, getStatusOptions } from '../model';

interface HistoryEntryProps {
  entry: ConsignmentHistoryEntry;
}

export function HistoryEntry({ entry }: HistoryEntryProps) {
  const { t } = useTranslation();

  const { statusOptions, cancellationReasonOptions } = useMemo(
    () => ({
      statusOptions: getStatusOptions(t),
      cancellationReasonOptions: getCancellationReasonOptions(t)
    }),
    [t]
  );

  return (
    <div className="border rounded-lg">
      <div className="px-4 py-2 bg-muted/50 border-b flex justify-between items-center">
        <span className="text-sm text-muted-foreground">
          {formatDistanceToNow(new Date(entry.timestamp), { addSuffix: true })}
        </span>
        <span className="text-sm font-medium">{format(new Date(entry.timestamp), 'dd/MMM/yyyy')}</span>
      </div>
      <div className="px-4 py-3 space-y-2">
        <p className="text-sm">{entry.description}</p>

        {entry.newStatusCode && (
          <div className="text-sm text-muted-foreground">
            <span className="font-medium">{t('newcmr.reqstatus.new.status.label')}:</span>
            {statusOptions[entry.newStatusCode]}
          </div>
        )}

        {entry.cancellationReason && (
          <div className="text-sm text-muted-foreground">
            <span className="font-medium">{t('newcmr.reqstatus.CA.cancellationReason.label')}:</span>
            {cancellationReasonOptions[entry.cancellationReason]}
          </div>
        )}

        {entry.changedBy && (
          <div className="text-sm text-muted-foreground">
            <span className="font-medium">{t('newcmr.reqstatus.changed.status.label')}:</span> {entry.changedBy}
          </div>
        )}
      </div>
    </div>
  );
}
